import { apiClient } from './api.client';

export interface Translation {
  id: string;
  page: string;
  section: string;
  element: string;
  textEN: string;
  textFR: string;
  label: string;
  description?: string;
  websiteUrl?: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
  User?: {
    id: string;
    firstName?: string;
    lastName?: string;
    email: string;
  };
}

export interface TranslationCreateInput {
  page: string;
  section: string;
  element: string;
  textEN: string;
  textFR: string;
  label: string;
  description?: string;
}

export interface TranslationUpdateInput {
  textEN?: string;
  textFR?: string;
  label?: string;
  description?: string;
}

export interface TranslationStats {
  total: number;
  byPage: Array<{
    page: string;
    count: number;
  }>;
}

export interface TranslationListResponse {
  success: boolean;
  data: Translation[];
  count: number;
}

export interface TranslationResponse {
  success: boolean;
  data: Translation;
  message?: string;
}

export interface TranslationStatsResponse {
  success: boolean;
  data: TranslationStats;
}

export class TranslationService {
  /**
   * Get all translations with optional filters
   */
  async getAll(params?: {
    page?: string;
    section?: string;
    search?: string;
  }): Promise<Translation[]> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page);
    if (params?.section) searchParams.append('section', params.section);
    if (params?.search) searchParams.append('search', params.search);

    const response = await apiClient
      .get(`translations?${searchParams.toString()}`)
      .json<TranslationListResponse>();

    return response.data;
  }

  /**
   * Get translations for a specific page
   */
  async getByPage(page: string): Promise<Translation[]> {
    const response = await apiClient
      .get(`translations/page/${page}`)
      .json<TranslationListResponse>();

    return response.data;
  }

  /**
   * Get single translation by ID
   */
  async getById(id: string): Promise<Translation> {
    const response = await apiClient
      .get(`translations/${id}`)
      .json<TranslationResponse>();

    return response.data;
  }

  /**
   * Get translation statistics
   */
  async getStats(): Promise<TranslationStats> {
    const response = await apiClient
      .get('translations/stats')
      .json<TranslationStatsResponse>();

    return response.data;
  }

  /**
   * Create a new translation
   */
  async create(data: TranslationCreateInput): Promise<Translation> {
    const response = await apiClient
      .post('translations', {
        json: data,
      })
      .json<TranslationResponse>();

    return response.data;
  }

  /**
   * Update an existing translation
   */
  async update(id: string, data: TranslationUpdateInput): Promise<Translation> {
    const response = await apiClient
      .put(`translations/${id}`, {
        json: data,
      })
      .json<TranslationResponse>();

    return response.data;
  }

  /**
   * Delete a translation
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`translations/${id}`);
  }

  /**
   * Bulk upsert translations
   */
  async bulkUpsert(
    translations: TranslationCreateInput[]
  ): Promise<{ created: number; updated: number }> {
    const response = await apiClient
      .post('translations/bulk', {
        json: { translations },
      })
      .json<{
        success: boolean;
        data: { created: number; updated: number };
        message: string;
      }>();

    return response.data;
  }
}

// Export singleton instance
export const translationService = new TranslationService();
